import { useQuery } from '@tanstack/react-query';

import { DataTable, DeadlinePill, EmptyState, type Column } from '@axioma/ui';

import { PageHeader } from '../components/PageHeader.js';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

interface TaskRow {
  id: string;
  title: string;
  actionType: string;
  legalDeadlineAt: string;
  internalDeadlineAt: string;
  deadlineState: 'GREEN' | 'YELLOW' | 'ORANGE' | 'RED' | 'MISSED';
  urgencyTier: string;
  process: { cnjNumberMasked: string; court: string };
  assignee?: { name: string } | null;
}

const columns: ReadonlyArray<Column<TaskRow>> = [
  {
    key: 'state',
    header: 'Estado',
    width: '110px',
    render: (row) => (
      <DeadlinePill
        state={row.deadlineState}
        label={formatRelative(row.legalDeadlineAt)}
        title={`Prazo legal: ${new Date(row.legalDeadlineAt).toLocaleString('pt-BR')}`}
      />
    ),
  },
  {
    key: 'title',
    header: 'Tarefa',
    render: (row) => (
      <div className="min-w-0">
        <p className="font-display text-sm text-ax-text-primary truncate">{row.title}</p>
        <p className="font-mono text-[11px] text-ax-text-tertiary truncate">
          {row.actionType}
        </p>
      </div>
    ),
  },
  { key: 'cnj', header: 'CNJ', mono: true, render: (row) => row.process.cnjNumberMasked },
  { key: 'court', header: 'Tribunal', render: (row) => row.process.court, hiddenOnCompact: true },
  {
    key: 'legal',
    header: 'Prazo legal',
    mono: true,
    render: (row) => new Date(row.legalDeadlineAt).toLocaleDateString('pt-BR'),
  },
  {
    key: 'internal',
    header: 'Prazo interno',
    mono: true,
    render: (row) => new Date(row.internalDeadlineAt).toLocaleDateString('pt-BR'),
    hiddenOnCompact: true,
  },
  {
    key: 'assignee',
    header: 'Responsável',
    render: (row) => row.assignee?.name ?? '—',
    hiddenOnCompact: true,
  },
];

export function Agenda(): JSX.Element {
  const tenant = useAuthStore((s) => s.currentTenantId);
  const query = useQuery({
    queryKey: ['vigilia', 'tasks', 'agenda', tenant],
    queryFn: async () => {
      const { data } = await api.get<{ items: TaskRow[]; total: number }>('/vigilia/tasks', {
        params: { pageSize: 100 },
      });
      return data;
    },
    enabled: Boolean(tenant),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Vigília"
        title="Agenda crítica"
        description="Tarefas judiciais ordenadas pelo prazo legal mais próximo. Atualização em tempo real."
      />
      <div className="px-8 py-6">
        {!query.isLoading && (query.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Nenhuma tarefa cadastrada"
            description="As tarefas são geradas automaticamente quando o classificador identifica movimentos acionáveis nos processos monitorados."
          />
        ) : (
          <DataTable
            columns={columns}
            data={query.data?.items ?? []}
            rowKey={(r) => r.id}
            loading={query.isLoading}
          />
        )}
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const target = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = target - now;
  if (diffMs < 0) {
    return 'Vencido';
  }
  const days = Math.floor(diffMs / 86_400_000);
  if (days >= 1) {
    return `${String(days)}d`;
  }
  const hours = Math.floor(diffMs / 3_600_000);
  return `${String(hours)}h`;
}
